import { useEffect, useState, type ReactNode } from 'react';
import { Button } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import { sourceWord } from '../flow/flow-item-words';
import { ProblemFileDetail } from './ProblemFileDetail';
import type { ProblemRowProps } from './ProblemRow';
import type { ProblemFile, ProblemFilesPage, ProblemGroup } from './problem-types';
import { actionWords, fileWords } from './problem-words';

export const FILES_A_PAGE = 25;

interface Loaded {
  key: string;
  page: ProblemFilesPage | null;
  failed: string | null;
}

const said = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** Reads a page of the row's files whenever the row, the page or the screen's stamp changes; the last page stays up meanwhile. */
function useFiles(
  load: ProblemRowProps['loadFiles'],
  group: ProblemGroup,
  offset: number,
  stamp: string,
): Loaded & { retry: () => void } {
  const [again, setAgain] = useState(0);
  const key = `${group.id}|${offset}|${stamp}|${again}`;
  const [loaded, setLoaded] = useState<Loaded>({ key: '', page: null, failed: null });
  useEffect(() => {
    let current = true;
    load(group, offset).then(
      (page) => current && setLoaded({ key, page, failed: null }),
      (e: unknown) => current && setLoaded({ key, page: null, failed: said(e) }),
    );
    return () => {
      current = false;
    };
    // The key carries everything the read depends on; the group object is new on every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, key]);
  return { ...loaded, retry: () => setAgain(again + 1) };
}

interface LineProps {
  group: ProblemGroup;
  file: ProblemFile;
  open: boolean;
  onOpen: () => void;
  onAsk: ProblemRowProps['onAsk'];
  onSee?: (() => void) | undefined;
}

function FileActions({ group, file, onAsk }: Omit<LineProps, 'open' | 'onOpen'>): ReactNode {
  if (!onAsk || file.settling) return null;
  return (
    <span className="pb-file__actions">
      {group.can
        .filter((a) => a !== 'acknowledge')
        .map((action) => (
          <Button
            key={action}
            size="sm"
            variant="ghost"
            onClick={() => onAsk({ group, action, files: [file] })}
          >
            {actionWords(group, action).one}
          </Button>
        ))}
    </span>
  );
}

const FILE_ICON = (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
    <path
      d="M4 1.5h5l3.5 3.5v9.5h-8.5z M9 1.5V5h3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </svg>
);

const EYE = (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
    <path
      d="M1.5 8s2.4-4.5 6.5-4.5S14.5 8 14.5 8s-2.4 4.5-6.5 4.5S1.5 8 1.5 8Z M8 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    />
  </svg>
);

function FileLine(props: LineProps): ReactNode {
  const { group, file, open, onOpen, onAsk, onSee } = props;
  const f = useFormat();
  return (
    <div className="pb-file__line">
      <button type="button" className="pb-file__name" aria-expanded={open} onClick={onOpen}>
        {FILE_ICON}
        <span>{fileWords(f, file)}</span>
      </button>
      <span className="pb-file__meta">
        {file.jobName ?? 'No job'} · {sourceWord(file.source)}
      </span>
      <span className="pb-file__num">
        {f.int(file.pages)} {file.pages === 1 ? 'page' : 'pages'}
      </span>
      <span className="pb-file__num">parked {f.duration(file.ageSeconds)}</span>
      {onSee ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={onSee}
          aria-label={`See ${file.name ?? 'the file'}`}
        >
          {EYE} See it
        </Button>
      ) : null}
      <FileActions group={group} file={file} onAsk={onAsk} />
    </div>
  );
}

interface PagerProps {
  page: ProblemFilesPage;
  offset: number;
  onOffset: (o: number) => void;
  f: Formatter;
}

function Pager({ page, offset, onOffset, f }: PagerProps): ReactNode {
  if (page.total <= FILES_A_PAGE) return null;
  const last = Math.min(page.total, offset + page.files.length);
  const back = () => onOffset(Math.max(0, offset - FILES_A_PAGE));
  return (
    <div className="pb-files__pager">
      <span>{`${f.int(offset + 1)} to ${f.int(last)} of ${f.int(page.total)}`}</span>
      <Button size="sm" variant="ghost" disabled={offset === 0} onClick={back}>
        Previous
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={last >= page.total}
        onClick={() => onOffset(offset + FILES_A_PAGE)}
      >
        Next
      </Button>
    </div>
  );
}

/** The files, one line each; the one that is open shows what happened to it underneath. */
function FileList({ page, row }: { page: ProblemFilesPage; row: ProblemRowProps }): ReactNode {
  const [openKey, setOpenKey] = useState<string | null>(null);
  return (
    <ul className="pb-files__list">
      {page.files.map((file) => {
        const open = openKey === file.key;
        return (
          <li key={file.key} className="pb-file" data-file={file.key} data-open={open}>
            <FileLine
              group={row.group}
              file={file}
              open={open}
              onOpen={() => setOpenKey(open ? null : file.key)}
              onAsk={row.onAsk}
              onSee={
                row.onSeeFile && file.kind === 'unit' && file.batchId
                  ? () => row.onSeeFile?.(row.group, file)
                  : undefined
              }
            />
            {open ? <ProblemFileDetail group={row.group} file={file} links={row.links} /> : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A row opened up: its files, oldest first, a page at a time. A file opens to what happened to it; each carries
 * the same controls as the row, for that file alone.
 */
export function ProblemFiles(row: ProblemRowProps): ReactNode {
  const f = useFormat();
  const [offset, setOffset] = useState(0);
  const { page, failed, retry } = useFiles(row.loadFiles, row.group, offset, row.stamp);
  if (failed)
    return (
      <p className="pb-files__note" role="alert">
        The files could not be read: {failed}{' '}
        <button type="button" className="pb-link" onClick={retry}>
          Try again
        </button>
      </p>
    );
  if (!page) return <p className="pb-files__note">Reading the files</p>;
  if (page.files.length === 0)
    return <p className="pb-files__note">Nothing is parked here any more.</p>;
  return (
    <div className="pb-files" data-files={row.group.id}>
      <FileList page={page} row={row} />
      <Pager page={page} offset={offset} onOffset={setOffset} f={f} />
    </div>
  );
}
